/**
 * Per-product price offers from affiliate partners.
 * Sorted cheapest-first when rendered. Product-specific `href` overrides the store default.
 */

import { stores } from "@/data/stores";
import type { MessageKey } from "@/lib/i18n";

export type OfferCurrency = "USD" | "EUR" | "PLN";

export type ProductOffer = {
  storeId: string;
  /** Omitted for browse-only partner rows (default store search link). */
  price?: number;
  currency?: OfferCurrency;
  /** Price includes shipping to a typical destination */
  shippingIncluded?: boolean;
  /** Optional product-specific affiliate URL; falls back to store.search href */
  href?: string;
  /** Checkout discount code (e.g. PBRICKS) */
  couponCode?: string;
  /** Percent off when couponCode is applied */
  couponPercent?: number;
};

/** Partners shown in Top Offers; missing entries get a browse-only row at the end. */
export const TOP_OFFER_PARTNER_IDS = [
  "afobrick",
  "barweer",
  "yourwobb",
  "aliexpress",
  "amazon-eu",
  "amazon-us",
] as const;

export function hasOfferPrice(offer: Pick<ProductOffer, "price">): offer is ProductOffer & {
  price: number;
  currency: OfferCurrency;
} {
  return typeof offer.price === "number" && Number.isFinite(offer.price);
}

export type ResolvedProductOffer = ProductOffer & {
  storeLabelKey: MessageKey;
  logoSrc: string;
  href: string;
};

const STORE_LOGOS: Record<string, string> = Object.fromEntries(
  stores.map((s) => [s.id, s.logoSrc])
);

/** Default checkout coupons for browse-only partner rows (store search links). */
const DEFAULT_STORE_COUPONS: Partial<
  Record<(typeof TOP_OFFER_PARTNER_IDS)[number], Pick<ProductOffer, "couponCode" | "couponPercent">>
> = {
  afobrick: { couponCode: "PBRICKS", couponPercent: 5 },
  yourwobb: { couponCode: "PBRICKS", couponPercent: 4 },
};

/** Offers keyed by product catalog number / id */
export const productOffersById: Record<string, ProductOffer[]> = {
  "95001": [
    {
      storeId: "afobrick",
      price: 41.8,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95001-classic-off-road-vehicle?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 45.36,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95001-classic-off-road-vehicle-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95002": [
    {
      storeId: "afobrick",
      price: 53.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95002-ferrari-trailer-set?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 50.43,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95002-trailer-set-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95003": [
    {
      storeId: "afobrick",
      price: 52.8,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95003-unimog-rv-trailer?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 54.49,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95003-rv-set-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95005": [
    {
      storeId: "afobrick",
      price: 49.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95005-car-series?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 51.44,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95005-building-block-car-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95006": [
    {
      storeId: "yourwobb",
      price: 30.69,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-95006-fast-furious-3-car-set?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 41.8,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95006-building-block-car?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 41.45,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95006-car-series-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95007": [
    {
      storeId: "yourwobb",
      price: 29.67,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-95007-stranger-things-two-car-set?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 41.8,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95007-building-block-car?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 37.39,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95007-dual-car-combination-movie?DIST=Q0ZGGFU%3D",
    },
  ],
  "95008": [
    {
      storeId: "afobrick",
      price: 46.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95008-tiger-tank-engine?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 43.21,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95008-tiger-tank-engine-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95009": [
    {
      storeId: "yourwobb",
      price: 54.27,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-95009-t34-v2-tank-engine?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 61.6,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95009-t34-v2-tank-engine?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 70.01,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95009-t34-v2-tank-engine-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95010": [
    {
      storeId: "yourwobb",
      price: 37.76,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-95010-sherman-gaa-v8-tank-engine?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 46.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95010-gaa-v8-tank-engine?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 47.39,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95010-gaa-v8-tank-engine-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95020": [
    {
      storeId: "afobrick",
      price: 66,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-f1-series?variant=67713834418405&ref=pbricks",
    },
  ],
  "95025": [
    {
      storeId: "afobrick",
      price: 66,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-f1-series?variant=67713834451173&ref=pbricks",
    },
  ],
  "95026": [
    {
      storeId: "afobrick",
      price: 46.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-f1-series?variant=67713834483941&ref=pbricks",
    },
  ],
  "95021": [
    {
      storeId: "yourwobb",
      price: 37.76,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-95021-bmw-m3-e30-dtm-race-team-set?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "aliexpress",
      price: 41.48,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c3cDocHz",
    },
    {
      storeId: "afobrick",
      price: 49.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95021-bmw-touring-car-grand-prix?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 50.43,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95021-car-series-building-block-car-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95022": [
    {
      storeId: "yourwobb",
      price: 35.72,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-95022-audi-rs6-ski-edition-caravan?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 44,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-95022-audi-rs6-caravan?ref=pbricks",
    },
    {
      storeId: "aliexpress",
      price: 49.93,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c3T6NfAx",
    },
    {
      storeId: "barweer",
      price: 45.36,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95022-towed-rv-ski-edition-technic?DIST=Q0ZGGFU%3D",
    },
  ],
  "95023": [
    {
      storeId: "yourwobb",
      price: 31.71,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-95023-american-vintage-repair-garage?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "aliexpress",
      price: 37.54,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c381YKhT",
    },
    {
      storeId: "barweer",
      price: 37.39,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-95023-garage-modular-buildings?DIST=Q0ZGGFU%3D",
    },
  ],
  "95027": [
    {
      storeId: "yourwobb",
      price: 23.81,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-95027-classic-land-rover-defender?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 30.8,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      // Afobrick slug says 97027; product numbering on page is 95027
      href: "https://afobrick.com/products/12go-97027-car-series?ref=pbricks",
    },
    {
      storeId: "aliexpress",
      price: 39.09,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c4Ev2zZf",
    },
  ],
  "95028": [
    {
      storeId: "yourwobb",
      price: 23.81,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-95028-universal-towable-rv?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 30.8,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      // Afobrick slug says 97028; product numbering on page is 95028
      href: "https://afobrick.com/products/12go-97028-car-series?ref=pbricks",
    },
    {
      storeId: "aliexpress",
      price: 39.09,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c4Ev2zZf",
    },
  ],
  "96001": [
    {
      storeId: "afobrick",
      price: 64.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96001-panther-kf51?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 52.85,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-96001-kf51-patnther-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "96002": [
    {
      storeId: "afobrick",
      price: 51.7,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96002-enhanced-main-battle-tank-embt?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 60.96,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-96002-enhanced-main-battle-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "96003": [
    {
      storeId: "afobrick",
      price: 53.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-t-14-armata-tank?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 61.98,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-96003-t-14-armata-main-battle-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "96005": [
    {
      storeId: "afobrick",
      price: 51.7,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96005-abrams-x?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 59.95,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-96005-abrams-x-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "96006": [
    {
      storeId: "afobrick",
      price: 46.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96006-stryker-x-armored-vehicle?ref=pbricks",
    },
  ],
  "96007": [
    {
      storeId: "afobrick",
      price: 44,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96007-m24-bradley-infantry-fighting-vehicle?ref=pbricks",
    },
  ],
  "96008": [
    {
      storeId: "afobrick",
      price: 44,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96008-zbl-19-wheeled-infantry-fighting-vehicle?ref=pbricks",
    },
  ],
  "96009": [
    {
      storeId: "afobrick",
      price: 41.8,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96009-unmanned-robot-dog?ref=pbricks",
    },
  ],
  "96010": [
    {
      storeId: "afobrick",
      price: 53.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96010-e-type-anti-aircraft-vehicle?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 71.03,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-96010-e-type-anti-aircraft-vehicle-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "96011": [
    {
      storeId: "afobrick",
      price: 69.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96011-kf-41-lynx-ifv?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 73.06,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-96011-kf-41-lvnxifv-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "96012": [
    {
      storeId: "yourwobb",
      price: 61.03,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-96012-german-leopard-2a8-main-battle-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 72.6,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-96012-leopard-2a8?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 68.69,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-96012-leopard-2a8-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97001": [
    {
      storeId: "afobrick",
      price: 44,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97001-tiger-tank-snow-paint?ref=pbricks",
    },
  ],
  "97002": [
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97002-panther-medium-tank-g?ref=pbricks",
    },
  ],
  "97003": [
    {
      storeId: "yourwobb",
      price: 33.69,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97003-pzkpfw-iv-ausf-f2?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97003-panzer-iv-f2?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 46.37,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97003-pzkpfw-iv-ausf-f2-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97005": [
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97005-kv-2?ref=pbricks",
    },
  ],
  "97006": [
    {
      storeId: "yourwobb",
      price: 45.92,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97006-is-2-heavy-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97006-is-2?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 46.37,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97006-is-2-heavy-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97007": [
    {
      storeId: "yourwobb",
      price: 33.68,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97007-t34-85-medium-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97007-t-34-85?ref=pbricks",
    },
  ],
  "97008": [
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97008-tiger-heavy-tank-electroplating-gray?ref=pbricks",
    },
  ],
  "97009": [
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97009-tiger-heavy-tank-electroplating-purple?ref=pbricks",
    },
  ],
  "97010": [
    {
      storeId: "yourwobb",
      price: 45.92,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97010-m4-sherman-medium-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 49.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97010-m4-sherman?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 47.73,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97010-m4-sherman-medium-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97011": [
    {
      storeId: "afobrick",
      price: 46.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97011-m26-pershing?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 47.73,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97011-m26-pershing-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97012": [
    {
      storeId: "yourwobb",
      price: 36.74,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97012-wittmann-s04-white-tiger-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97012-tiger-tank-s04?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 49.41,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97012-tiger-tank-no04s-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97013": [
    {
      storeId: "yourwobb",
      price: 37.77,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97013-tiger-tank-007?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97013-tiger-tank-007?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 48.4,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97013-tiger-tank-007-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97015": [
    {
      storeId: "afobrick",
      price: 49.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97015-tiger-tank-no-1322?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 48.4,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97015-tiger-tank-no1322-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97016": [
    {
      storeId: "yourwobb",
      price: 60.14,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97016-panzer-viii-maus?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 75.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97016-maus-tank?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 68.12,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97016-panzer-viii-maus-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97017": [
    {
      storeId: "yourwobb",
      price: 55.04,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97017-e100wt-weapon-transport-vehicle?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 68.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97017-waffentrager-e-100?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 70.64,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97017-e-100-wt-tank-128-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97018": [
    {
      storeId: "afobrick",
      price: 68.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97018-jagopanzer-e-100?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 67.59,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97018-jagopanzer-e-100-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97019": [
    {
      storeId: "afobrick",
      price: 68.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97019-e-100-heavy-tank?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 68.61,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97019-e-100-heavy-tank-128-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97020": [
    {
      storeId: "afobrick",
      price: 68.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97020-e-100-heavy-tank-maus-turret?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 69.62,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97020-e-100-heavy-tank-128-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97021": [
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97021-jagdpanzer-tiger-p?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 49.6,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97021-jagdpanzer-tiger-p-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97022": [
    {
      storeId: "yourwobb",
      price: 60.77,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97022-jagdpanzer-tiger-p?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 79.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97022-jagdpanzer-tiger-p-collectors-edition?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 72.11,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97022-jagdpanzer-tiger-p-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97023": [
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97023-jagdpanzer-tiger-p-elephant-camouflage-version?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 59.56,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97023-jagdpanzer-tiger-p-elephant-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97025": [
    {
      storeId: "yourwobb",
      price: 63.53,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97025-panzer-viii-maus?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 75.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97025-maus-tank-camouflage-version?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 81.09,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97025-panzer-viii-maus-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97026": [
    {
      storeId: "aliexpress",
      price: 104.23,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c3lfAFy1",
    },
    {
      storeId: "yourwobb",
      price: 113.54,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97026-karl-gerat?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 152.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97026-karl-gerat?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 132.45,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97026-karl-gerat-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97027": [
    {
      storeId: "yourwobb",
      price: 46.24,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97027-german-tiger-ii-heavy-tank-wwii?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 53.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97027-tiger-ii?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 59.56,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97027-tiger-ii-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97029": [
    {
      storeId: "yourwobb",
      price: 43.18,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97029-world-war-ii-german-porsche-tiger-tank-p?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 53.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97029-tiger-p?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 59.56,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97029-tiger-tank-p-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97030": [
    {
      storeId: "yourwobb",
      price: 36.75,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97030-panzer-iv-h?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 47.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97030-panzer-iv-h?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 49.41,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97030-panzer-iv-h-tank-128-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97031": [
    {
      storeId: "yourwobb",
      price: 34.71,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97031-wirbelwind-flakpanzer-iv?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 44,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97031-wirbelwind?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 47.39,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97031-wirbelwind-flakpanzer-iv-tank-128-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97032": [
    {
      storeId: "yourwobb",
      price: 48.15,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97032-type-99b-main-battle-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 57.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97032-96b?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 64.94,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97032-type-99b-main-battle-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97033": [
    {
      storeId: "yourwobb",
      price: 54.02,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97033-type-100-main-battle-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 68.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97033-type-100?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 72.66,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97033-type-100-main-battle-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97036": [
    {
      storeId: "yourwobb",
      price: 212.07,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97036-1-16-tiger-ii-bricks-set?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "aliexpress",
      price: 260.07,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c4kp5IUv",
    },
    {
      storeId: "afobrick",
      price: 273.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97036-1-16-tiger-ii-tank?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 277.83,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97036-tiger-ii-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97037": [
    {
      storeId: "afobrick",
      price: 74.8,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97037-rivers-gantry-crane?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 75.09,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97037-rivers-gantry-crane-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97038": [
    {
      storeId: "yourwobb",
      price: 53,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97038-british-fv4005-stage-ii?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 60.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97038-fv4005-stage-ii?ref=pbricks",
    },
    {
      storeId: "aliexpress",
      price: 61.58,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c3W9DvuJ",
    },
    {
      storeId: "barweer",
      price: 70.97,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97038-fv4005-stage-ii-128-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97039": [
    {
      storeId: "yourwobb",
      price: 44.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97039-strv-103b-main-battle-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 61.6,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97039-strv-103b?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 59.56,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97039-strv-103b-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "97059": [
    {
      storeId: "yourwobb",
      price: 44.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-97059-panther-ii?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "aliexpress",
      price: 51.77,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c3aQbqMh",
    },
    {
      storeId: "afobrick",
      price: 53.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97059-panther-ii?ref=pbricks",
    },
  ],
  "97056": [
    {
      storeId: "aliexpress",
      price: 67.67,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c3JxyC01",
    },
    {
      storeId: "yourwobb",
      price: 51.98,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-97056-fv215-tank-destroyer?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "barweer",
      // Product $37.18 + shipping to PL $32.46 (China air)
      price: 69.64,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97056-fv215-tank-destroyer-military?DIST=Q0ZGGFU%3D",
    },
    {
      storeId: "afobrick",
      price: 61.6,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97056-fv215-tank-destroyer?ref=pbricks",
    },
  ],
  "97066": [
    {
      storeId: "afobrick",
      price: 46.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97066-sturmgeschutz-iv?ref=pbricks",
    },
  ],
  "97060": [
    {
      storeId: "yourwobb",
      price: 46.24,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-97060-jagdtiger-tank-destroyer?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "aliexpress",
      price: 48.71,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c2ITkyaN",
    },
    {
      storeId: "afobrick",
      price: 57.2,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97060-jagdtiger?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 61.59,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97060-jagdtiger-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98001": [
    {
      storeId: "afobrick",
      price: 82.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98001-panzer-viii-maus-heavy-tank?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 87.18,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98001-panzer-viii-maus-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98002": [
    {
      storeId: "yourwobb",
      price: 70.21,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-98002-panzer-vii-maus?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 82.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98002-panzer-viii-maus-land-cruiser-version?ref=pbricks",
    },
  ],
  "98003": [
    {
      storeId: "afobrick",
      price: 64.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98003-kv-2-heaty-tank?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 66.97,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98003-kv-2-heavy-tank-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98005": [
    {
      storeId: "yourwobb",
      price: 69.95,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-98005-bismarck-class-battleship?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 93.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98005-bismarck-1-300?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 77.37,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98005-bismarck-battleship-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98006": [
    {
      storeId: "yourwobb",
      price: 70.97,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-98006-tirpitz-battleship?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 93.5,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98006-tirpitz-battleship?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 76.62,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98006-tirpitz-battleship-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98007": [
    {
      storeId: "yourwobb",
      price: 80.66,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-98007-uss-missouri?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 94.6,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98007-uss-missouri?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 64.2,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98007-uss-missouri-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98008": [
    {
      storeId: "afobrick",
      price: 94.6,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98008-the-north-carolina?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 75.87,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-the-north-carolina-1300-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98009": [
    {
      storeId: "yourwobb",
      price: 82.7,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-98009-uss-montana?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 108.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98009-uss-montana?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 82.32,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98009-uss-montana-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98010": [
    {
      storeId: "yourwobb",
      price: 61.03,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-98010-uss-colorado?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 94.6,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98010-uss-colorado?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 69.45,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98010-uss-colorado-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98013": [
    {
      storeId: "yourwobb",
      price: 182.88,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/products/12go-98013-type-076-amphibious-assault-ship?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 174.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97013-type-075-amphibious-assault-ship?ref=pbricks",
    },
    {
      storeId: "barweer",
      price: 127.07,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98013-type-076-amphibious-assault-ship-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "98015": [
    {
      storeId: "aliexpress",
      price: 95.95,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c2QBE2g1",
    },
    {
      storeId: "yourwobb",
      price: 96.08,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-98015-u-b0ot-u-96?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 130.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98015-u-boot-u-96?ref=pbricks",
    },
  ],
  "98016": [
    {
      storeId: "afobrick",
      price: 163.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-98016-1-300-uss-enterprise-cv-6?ref=pbricks",
    },
    {
      storeId: "barweer",
      // Product $86.87 + shipping to PL $57.11 (China air)
      price: 143.98,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-98016-uss-enterprise-cv-6-1300-military?DIST=Q0ZGGFU%3D",
    },
    {
      storeId: "yourwobb",
      price: 119.66,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-98016-uss-enterprise-cv-6?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "aliexpress",
      price: 165.65,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c387poOV",
    },
  ],
  "91013": [
    {
      storeId: "yourwobb",
      price: 25.59,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-91013-lynx-tactical-atv?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "aliexpress",
      price: 39.85,
      currency: "USD",
      shippingIncluded: true,
      href: "https://s.click.aliexpress.com/e/_c4mkocSV",
    },
  ],
  "97055": [
    {
      storeId: "yourwobb",
      price: 46.11,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/12go-97055-leopard-1-medium-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
    {
      storeId: "afobrick",
      price: 53.9,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/12go-97055-leopard-1-medium-tank?ref=pbricks",
    },
    {
      storeId: "barweer",
      // Product $32.58 + shipping $29.50 (China air)
      price: 62.08,
      currency: "USD",
      shippingIncluded: true,
      href: "https://www.barweer.com/products/12go-97055-leopard-1-medium-tank-128-military?DIST=Q0ZGGFU%3D",
    },
  ],
  "35001": [
    {
      storeId: "afobrick",
      price: 25.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/brixago-35001-elefant-tank-destroyer1?ref=pbricks",
    },
    {
      storeId: "yourwobb",
      // Goods $10.23 + shipping $19.56
      price: 29.79,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/ly-35001-1-35-elefant-tank-destroyer?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
  ],
  "35002": [
    {
      storeId: "afobrick",
      price: 25.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/brixago-35002-panther?ref=pbricks",
    },
    {
      storeId: "yourwobb",
      // Goods $10.23 + shipping $18.42
      price: 28.65,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/ly-35002-1-35-panther-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
  ],
  "35003": [
    {
      storeId: "afobrick",
      price: 25.3,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 5,
      href: "https://afobrick.com/products/brixago-35003-tigerr-tank?ref=pbricks",
    },
    {
      storeId: "yourwobb",
      // Goods $10.23 + shipping $18.42
      price: 28.65,
      currency: "USD",
      shippingIncluded: true,
      couponCode: "PBRICKS",
      couponPercent: 4,
      href: "https://yourwobb.com/collections/new-arrivals/products/ly-35003-1-35-tiger-p-tank?utm_source=app_affiliate&utm_medium=3FA55BC1A8CD50497F68EC134605EA2A&coupon_code=PBricks&data_from=app_affiliate",
    },
  ],
};

function partnerOrderIndex(storeId: string): number {
  const index = TOP_OFFER_PARTNER_IDS.indexOf(storeId as (typeof TOP_OFFER_PARTNER_IDS)[number]);
  return index === -1 ? TOP_OFFER_PARTNER_IDS.length : index;
}

export function getProductOffers(productId: string): ResolvedProductOffer[] {
  const raw = productOffersById[productId] ?? [];
  const resolved: ResolvedProductOffer[] = [];
  const presentStoreIds = new Set<string>();

  for (const offer of raw) {
    const store = stores.find((s) => s.id === offer.storeId);
    if (!store) continue;
    presentStoreIds.add(offer.storeId);
    resolved.push({
      ...offer,
      storeLabelKey: store.labelKey,
      logoSrc: STORE_LOGOS[offer.storeId] ?? `/images/partners/${offer.storeId}.png`,
      href: offer.href?.trim() || store.href,
    });
  }

  for (const storeId of TOP_OFFER_PARTNER_IDS) {
    if (presentStoreIds.has(storeId)) continue;
    const store = stores.find((s) => s.id === storeId);
    if (!store) continue;
    resolved.push({
      storeId,
      storeLabelKey: store.labelKey,
      logoSrc: STORE_LOGOS[storeId] ?? `/images/partners/${storeId}.png`,
      href: store.href,
      ...DEFAULT_STORE_COUPONS[storeId],
    });
  }

  return resolved.sort((a, b) => {
    const aPriced = hasOfferPrice(a);
    const bPriced = hasOfferPrice(b);
    if (aPriced && bPriced) return a.price - b.price;
    if (aPriced) return -1;
    if (bPriced) return 1;
    return partnerOrderIndex(a.storeId) - partnerOrderIndex(b.storeId);
  });
}

export function formatOfferPrice(price: number, currency: OfferCurrency): string {
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(price);
  } catch {
    return `${currency} ${price.toFixed(2)}`;
  }
}
